import React, { useState, Fragment } from 'react';
import styled from 'styled-components';
import { Plus, Check } from 'library/icons';
import Query from 'library/util/query';
import { jsonToInputParams } from 'library/util/graph';
import { withUser } from 'util/hoc';
import Env from 'library/util/env';
import { resolve } from 'library/helpers/path-resolver';
import { NotificationRender } from 'library/components/notification';
import { ActionButton } from 'apps/journey/pages/professional-center/components/action-button';
import { withRouter } from 'library/util/core-router';
import setDynamicUserDataMutation from 'apps/journey/queries/setDynamicUserDataMutation';

const RejectSvg = styled(Plus)`
  transform: rotate(45deg);
`;

const updateDynamoTimestamp = async (
  configuration,
  approving_user,
  user,
  org_id,
  action
) => {
  const event_name = `professional_${action}_${org_id}`;
  const env_name = Env.get('__ENV__', '');

  const item_params = { data: {}, uuid: user.uuid };
  item_params['data'][`${action}_by`] = `${approving_user.uuid}`;
  item_params['data']['env_name'] = env_name;

  const response = await fetch(
    `${Env.get('__EVENTS_APIGATEWAY_ENDPOINT__')}/${event_name}`,
    {
      method: 'POST',
      body: JSON.stringify(item_params),
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );
};

const updateUserDataTimestamp = async (org_id, user, type) => {
  try {
    const response = await setDynamicUserDataMutation({
      attributeName: `professional_${type}_${org_id}`,
      attributeData: Date.now().toString(),
      user: user,
    });
    if (!resolve(response, 'success', false)) {
      NotificationRender('error', 'Something went wrong, please try again.');
      console.error(response);
    }
  } catch (e) {
    NotificationRender('error', 'Something went wrong, please try again.');
    console.error(e);
  }
};

const getUserPermission = (organization) => {
  let userPermissionString = '';
  const isProfessional = resolve(organization, 'professional', '');
  const isProfessionalAdmin = resolve(organization, 'professional_admin', '');
  userPermissionString = isProfessional ? 'Professional' : '';
  userPermissionString = isProfessionalAdmin ? 'Admin' : userPermissionString;
  return userPermissionString;
};

export const getApprovalStatusString = (approved, organization) => {
  const approvalStatus = resolve(organization, 'approval_status', '');
  let approvalStatusString = '';

  if (approved) {
    approvalStatusString = getUserPermission(organization);
  } else if (approvalStatus.toLowerCase() === 'denied') {
    approvalStatusString = 'Denied';
  } else {
    approvalStatusString = 'Awaiting Approval';
  }
  return approvalStatusString;
};

const OrgApprovalOptions = (user) =>
  withRouter(
    withUser(
      ({
        record: orgRecord,
        user: approving_user,
        configuration,
        reload = () => null,
      }) => {
        const [loading, setLoading] = useState(false);

        const { id, professional } = orgRecord;
        const approvalStatus = getApprovalStatusString(professional, orgRecord);
        const handleApprovalRemoval = async (e) => {
          e.preventDefault();
          e.stopPropagation();

          setLoading(true);
          const approvalType = e.target.name;
          try {
            if (
              approvalType === 'Approve' ||
              approvalType === 'Approve_Admin'
            ) {
              const approval_type =
                approvalType === 'Approve_Admin'
                  ? 'ProfessionalAdmin'
                  : 'Professional';

              const params = {
                org_id: id,
                user_uuids: [user.uuid],
                user_type: approval_type,
              };

              const { data } = await Query(`
            mutation {
              add_professional_roles_to_org
                 (${jsonToInputParams(params)}) {
                success,
                errors
              }
            }
          `);

              if (data?.add_professional_roles_to_org?.success) {
                await updateDynamoTimestamp(
                  configuration,
                  approving_user,
                  user,
                  id,
                  'approved'
                );
                await updateUserDataTimestamp(id, user, 'approved');
              }
            } else {
              const params = {
                org_id: id,
                user_uuids: [user.uuid],
                user_type: 'Professional',
              };

              const { data } = await Query(`
            mutation {
              remove_professional_roles_from_org
                 (${jsonToInputParams(params)}) {
                success,
                errors
              }
            }
          `);

              if (data?.remove_professional_roles_from_org?.success || false) {
                await updateDynamoTimestamp(
                  configuration,
                  approving_user,
                  user,
                  id,
                  'denied'
                );
                await updateUserDataTimestamp(id, user, 'denied');
                await Query(`
              mutation {
                email_professional_request_denied(${jsonToInputParams({
                  uuid: user.uuid,
                })}) {
                  success
                  errors
                }
              }
            `);
              }
            }
          } catch (e) {
            console.error(e);
          }
          await reload();
          setLoading(false);
        };

        if (
          approvalStatus === 'Approved' ||
          approvalStatus === 'Professional'
        ) {
          return (
            <Fragment>
              <ActionButton
                onClick={handleApprovalRemoval}
                name="Approve_Admin"
                width="115px"
                disabled={loading}
              >
                Promote to Admin
              </ActionButton>
              <ActionButton
                onClick={handleApprovalRemoval}
                name="Remove"
                actionName="Remove"
                iconComponent={RejectSvg}
                width="97px"
                disabled={loading}
              >
                Revoke
              </ActionButton>
            </Fragment>
          );
        }
        if (approvalStatus === 'Denied') {
          return (
            <ActionButton
              onClick={handleApprovalRemoval}
              name="Approve"
              iconComponent={Check}
              width="97px"
              iconSize=".75em"
              disabled={loading}
            >
              Approve
            </ActionButton>
          );
        }
        return (
          <Fragment>
            <ActionButton
              onClick={handleApprovalRemoval}
              name="Approve"
              iconComponent={Check}
              width="97px"
              iconSize=".75em"
              disabled={loading}
            >
              Approve
            </ActionButton>
            <ActionButton
              onClick={handleApprovalRemoval}
              name="Deny"
              iconComponent={RejectSvg}
              width="75px"
              disabled={loading}
            >
              Deny
            </ActionButton>
          </Fragment>
        );
      }
    )
  );

export default OrgApprovalOptions;
